import { useEffect, useMemo, useState } from 'react';
import Calendar from './Calendar';
import DayTimeline, { TimeRange } from '../shared/DayTimeline';
import { DayTimelineRequest, requestOnDay, SNAP_MINUTES, formatTime } from '../shared/dayTimelineUtils';

interface AfterHoursRequestFormProps {
  selectedArea: string | null;
  existingRequests?: DayTimelineRequest[];
  submitRequest: (startDateTime: number, endDateTime: number, reason: string) => Promise<void>;
}

function getDefaultRange(date: Date): TimeRange {
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const start = new Date(date);
  if (isToday) {
    const minutes = now.getMinutes();
    const rounded = Math.ceil((minutes + 1) / SNAP_MINUTES) * SNAP_MINUTES;
    start.setHours(now.getHours() + Math.floor(rounded / 60), rounded % 60, 0, 0);
  } else {
    start.setHours(18, 0, 0, 0);
  }
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  return { startMs: start.getTime(), endMs: end.getTime() };
}

function buildSummaryText(selectedArea: string, date: Date, selectedRange: TimeRange, reason: string): string {
  const formattedDate = date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const durationHours = (selectedRange.endMs - selectedRange.startMs) / (1000 * 60 * 60);
  const durationText =
    durationHours === 1
      ? '1 hour'
      : durationHours < 1
        ? `${durationHours * 60} minutes`
        : durationHours % 1 === 0
          ? `${durationHours} hours`
          : `${durationHours.toFixed(1)} hours`;

  const reasonSuffix = reason.trim() ? ` Reason: ${reason.trim()}.` : '';
  return `${selectedArea} on ${formattedDate} from ${formatTime(selectedRange.startMs)} to ${formatTime(selectedRange.endMs)} (${durationText}).${reasonSuffix}`;
}

export default function AfterHoursRequestForm({
  selectedArea,
  existingRequests = [],
  submitRequest,
}: AfterHoursRequestFormProps) {
  const [date, setDate] = useState<Date>(new Date());
  const [selectedRange, setSelectedRange] = useState<TimeRange | null>(() => getDefaultRange(new Date()));
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sentDetails, setSentDetails] = useState<string | null>(null);
  const [error, setError] = useState('');

  const inputsDisabled = submitting || !selectedArea;

  const requestsForDay = useMemo(
    () => existingRequests.filter((request) => requestOnDay(request, date)),
    [existingRequests, date],
  );

  useEffect(() => {
    setSelectedRange(getDefaultRange(date));
  }, [date]);

  const resetForm = () => {
    const today = new Date();
    setDate(today);
    setSelectedRange(getDefaultRange(today));
    setReason('');
    setSentDetails(null);
    setError('');
    setSubmitting(false);
  };

  const submit = async () => {
    if (!selectedRange || !selectedArea || submitting) return;
    if (selectedRange.endMs <= selectedRange.startMs) return;

    const details = buildSummaryText(selectedArea, date, selectedRange, reason);
    setSubmitting(true);
    setError('');

    try {
      await submitRequest(selectedRange.startMs, selectedRange.endMs, reason);
      setSentDetails(details);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to submit request';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const summaryText =
    selectedArea && selectedRange ? buildSummaryText(selectedArea, date, selectedRange, reason) : '';

  return (
    <div className="ah-card">
      <div className="ah-title">{sentDetails ? 'Request Sent' : 'New After Hours Request'}</div>

      {sentDetails ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}>
          <div
            style={{
              padding: '16px',
              backgroundColor: '#f0fff4',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#22543d',
              border: '1px solid #9ae6b4',
              lineHeight: '1.5',
            }}
          >
            <strong>Request sent.</strong> Details: {sentDetails}
          </div>
          <button
            className="ah-btn ah-btn-outline"
            onClick={resetForm}
            style={{
              width: '100%',
              minHeight: '48px',
              fontSize: '16px',
              fontWeight: '500',
            }}
          >
            Create new request
          </button>
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            width: '100%',
            opacity: submitting ? 0.55 : 1,
            transition: 'opacity 0.2s ease',
          }}
        >
          <div style={{ width: '100%' }}>
            <label className="muted" style={{ display: 'block', marginBottom: 8 }}>
              Select Date
            </label>
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <Calendar
                selectedDate={date}
                onDateSelect={setDate}
                maxDaysFuture={29}
                disabled={inputsDisabled}
              />
            </div>
          </div>

          <div style={{ width: '100%' }}>
            <label className="muted" style={{ display: 'block', marginBottom: 8 }}>
              Select Time (drag on timeline)
            </label>
            <DayTimeline
              day={date}
              requests={requestsForDay}
              selectedRange={selectedRange}
              onRangeChange={setSelectedRange}
              interactive
              disabled={inputsDisabled}
              title="Drag to choose your request window"
            />
          </div>

          <div style={{ width: '100%' }}>
            <label className="muted" style={{ display: 'block', marginBottom: 4, fontSize: '14px' }}>
              Reason
            </label>
            <input
              className="ah-input"
              type="text"
              placeholder="Optional"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={inputsDisabled}
              style={{
                width: '100%',
                minHeight: '44px',
                fontSize: '16px',
                opacity: inputsDisabled ? 0.7 : 1,
                cursor: inputsDisabled ? 'not-allowed' : 'text',
              }}
            />
          </div>

          {summaryText && (
            <div
              style={{
                padding: '12px 16px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#495057',
                border: '1px solid #e9ecef',
                lineHeight: '1.4',
                textAlign: 'center',
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              {summaryText}
            </div>
          )}

          {error && (
            <div
              style={{
                color: '#e53e3e',
                backgroundColor: '#fed7d7',
                border: '1px solid #feb2b2',
                borderRadius: 6,
                padding: 12,
                fontSize: 14,
              }}
            >
              {error}
            </div>
          )}

          <div style={{ width: '100%' }}>
            <button
              className="ah-btn"
              onClick={submit}
              disabled={!selectedArea || !selectedRange || submitting}
              style={{
                width: '100%',
                minHeight: '48px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: submitting ? 'wait' : undefined,
              }}
            >
              {submitting ? 'Sending request…' : 'Submit Request'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
